import {
  randomItem,
  type QueriesObject,
  type QueryResults,
  type System,
  randomShuffle,
} from '../../odie';
import type { GameScene } from '../GameScene';
import { BlockComponent } from '../components/BlockComponent';
import {
  getBaseScore,
  getScoringConfig,
} from 'src/replicant/match2/match2.getters';
import { BlockEntity } from '../entities/BlockEntity';
import { animDuration, waitForItPromise } from 'src/lib/utils';
import { BlockId, powerBlockTypes } from '../defs/block';
import { isPowerBlock } from '../util/matchTools';
import { Match2ScoreProgressbarNew } from '../../components/Match2ScoreProgressbarNew';
import { matchTheme } from 'src/game/theme/match2/Match2Theme';
import { user } from 'src/StateObserver';
import {
  Match2ScoringConfig,
  match2Scoring,
} from 'src/replicant/match2/match2.ruleset';

/*
  Handles game scoring
*/
export class ScoreSystem implements System {
  public static readonly NAME = 'score';
  public static Queries: QueriesObject = {
    block: {
      components: [BlockComponent],
      removed: true,
    },
  };

  public scene!: GameScene;
  public queries!: QueryResults;
  private invisible = false;

  private _scoringConfig: Match2ScoringConfig = match2Scoring;

  public get score() {
    return this._score;
  }

  public get stars() {
    return this.starsPercentage.filter(
      (p) => this._score >= p * this._baseScore,
    ).length;
  }

  public get starsPercentage() {
    return this._scoringConfig.starsPercentage.map((p) => {
      // Round to the nearest 10,000.
      const rounded = Math.round((p * this._baseScore) / 10_000) * 10_000;

      // Convert back to percentage.
      return rounded / this._baseScore;
    });
  }

  public get baseScore() {
    return this._baseScore;
  }

  public get scoringConfig() {
    return this._scoringConfig;
  }

  public progressBar: Match2ScoreProgressbarNew | undefined;
  private finalScore = false;
  private scoreBeforeBonus = 0;
  private _score = 0;
  private _baseScore = 0;
  private interrupted = false;

  public init() {
    if (this.progressBar) return;
    this.progressBar = matchTheme().getScoreProgressbar({
      // Add the scorebar to the superview so the puzzle stage can be highlighted isolated from this view.
      superview: this.scene.stage.view.getSuperview()!,
      bgStyle: {
        zIndex: -1,
      },
    });
    this.scene.events.subscribe((e) => {
      if (e?.id !== 'destroy' && e?.id !== 'poke') return;
      this.onBlockDestroyed();
    });
  }

  public start() {
    this._scoringConfig = getScoringConfig(user());
    const baseScore = getBaseScore(
      this._scoringConfig,
      this.scene.getMovesLimit(),
    );
    this._baseScore = baseScore;
    this.progressBar!.domain = [
      0,
      // The bar should end at the last star.
      baseScore * this.starsPercentage[this.starsPercentage.length - 1],
    ];
    this.progressBar!.starsPercentage = this.starsPercentage;

    // Auto update score if it starts higher than 0, like when loading a saved game.
    if (this._score > 0) {
      this.updateScore(this._score);
    }
  }

  public async handleVictory() {
    // Disable input.
    this.scene.phaseSystem.setInputDisable(true);

    // Set flags for the animation.
    this.finalScore = true;
    this.scoreBeforeBonus = this._score;

    // Update score, add bonus for unused moves.
    this._score += this._scoringConfig.unusedMovesScore * this.scene.moves;

    // Skip if score system should be invisible.
    if (this.invisible) return;

    // Create a promise that will resolve when the user taps the screen.
    const tapPromise = new Promise<void>((resolve) =>
      this.scene.stage.view.getSuperview()?.once('InputStart', () => {
        this.interrupted = true;
        resolve();
      }),
    );

    // Interrupt the animation if the user taps the screen.
    await Promise.race([this.victoryAnimation(), tapPromise]);

    // Scene already destroyed, stop here.
    if (!this.scene) return;

    // Make sure moves and score reflect the final state.
    this.scene.visualMoves = this.scene.moves;
    this.scene.events.publish({ id: 'updateHud' });
    this.progressBar?.animateProgress(this._score);
  }

  public getScore() {
    return this._score;
  }

  public setScore(score: number, scoringConfig?: Match2ScoringConfig) {
    this._score = score;
    this._scoringConfig = scoringConfig ?? this._scoringConfig;
  }

  public updateScore(score: number) {
    this._score = score;
    this.progressBar?.animateProgress(this._score);
  }

  public onBlockDestroyed() {
    if (this.finalScore) return;
    this.updateScore(this._score + this._scoringConfig.blockDestroyed);
  }

  public reset() {
    this.finalScore = false;
    this._score = 0;
    this._baseScore = 0;
    this.interrupted = false;
    if (this.progressBar) {
      this.progressBar.progress = 0;
    }
  }

  private async victoryAnimation() {
    // Skip if score system should be invisible.
    if (this.invisible) return;

    // Select random entities based on the number of moves.
    const blockEntities = this.queries.block.entities as BlockEntity[];
    const powerBlocks = blockEntities.filter((x) => isPowerBlock(x));
    const entities = randomShuffle([
      ...blockEntities.filter((x) => x.c.block.props.type === 'basic'),
    ] as BlockEntity[]).slice(0, this.scene.moves);

    // Will store the new blocks.
    const promises = [];

    // Replace entities with fireworks and bombs.
    let delay = animDuration;
    const decrement = delay * 0.05;
    for (const entity of entities) {
      if (this.interrupted) return;

      const blockId = randomItem<BlockId>([
        'bomb',
        'rocketHorizontal',
        'rocketVertical',
      ]);
      const promise = this.scene.basicBlockSystem
        .turnBlockInto(entity, blockId)
        .then(async (block) => {
          // FIXME: There's an additional animation which is not awaited when mutating a block, so we should wait for it here.
          await waitForItPromise(200);
          return block;
        })
        .finally(() => {
          if (this.interrupted) return;
          this.scene.visualMoves++;
          this.scene.events.publish({ id: 'updateHud' });
        });

      promises.push(promise);

      // Delay shorter for each block.
      await waitForItPromise(delay);
      delay = Math.max(delay - decrement, 0);
    }

    const blocks = await Promise.all(promises);

    // Get all power blocks.
    const allPowerBlocks = [...blocks, ...powerBlocks];

    // Sort power blocks based on importance.
    allPowerBlocks.sort((a, b) => {
      return (
        powerBlockTypes.findIndex((x) => a.c.block.props.type.includes(x)) -
        powerBlockTypes.findIndex((x) => b.c.block.props.type.includes(x))
      );
    });

    // Trigger power blocks.
    const animationScoreStep =
      (this.score - this.scoreBeforeBonus) / allPowerBlocks.length;
    let animationScore = this.scoreBeforeBonus;
    for (const entity of allPowerBlocks) {
      if (this.interrupted) return;
      const block = entity as BlockEntity;

      block.c.block.collide('attack');

      // Animate post-victory bonus unused moves score, splitted into and at the trigger of each bonus power block.
      animationScore += animationScoreStep;
      this.progressBar?.animateProgress(animationScore);

      await waitForItPromise(animDuration / 2);

      if (this.interrupted) return;

      // Board has settled, stop the animation.
      if (this.scene.phaseSystem.phase === 'input') {
        break;
      }
    }

    // Wait for board to settle.
    if (this.scene.phaseSystem.phase !== 'input') {
      await this.scene.phaseSystem.waitForPhase('input');
    }
  }

  public destroy() {
    this.interrupted = true;
  }

  public setInvisible(invisible: boolean) {
    this.invisible = invisible;
    this.progressBar?.updateOpts({
      visible: !invisible,
    });
  }
}
